<script>
  // # # # # # # # # # # # # #
  //
  //  Fullscreen slideshow
  //
  // # # # # # # # # # # # # #

  // *** IMPORT
  import { onMount } from "svelte";
  import { createEventDispatcher } from "svelte";
  import Flickity from "flickity-imagesloaded";

  // *** COMPONENTS
  import Video from "./Video.svelte";

  // *** PROPS
  export let slides = [];
  export let active = true;
  export let startSlide = 0;

  // *** DOM REFERENCES
  let slideShowEl = {};

  // *** VARIABLES
  let flkty = {};

  // *** CONSTANTS
  const options = {
    wrapAround: true,
    prevNextButtons: false,
    pageDots: false,
    groupCells: 1,
    imagesLoaded: true,
    selectedAttraction: 0.028,
    friction: 0.28,
    initialIndex: startSlide
  };

  $: {
    if (flkty.selectCell && !active) {
      console.log(startSlide);
      flkty.selectCell(startSlide === 0 ? 0 : startSlide + 1, true, true);
    }
  }

  const paddedSlides = slides.length < 8 ? [...slides, ...slides] : slides;

  const dispatch = createEventDispatcher();

  const closeSlideshow = () =>
    dispatch("close", {
      index: flkty.selectedIndex
    });

  // *** ON MOUNT
  onMount(async () => {
    if (slideShowEl) {
      flkty = new Flickity(slideShowEl, options);
    }
  });
</script>

<style lang="scss">
  @import "../_variables.scss";

  .slideshow {
    $block: &;
    background: white;

    opacity: 0;
    pointer-events: none;
    transition: opacity $fast-easing;

    height: 100vh;
    width: 100vw;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2000;

    &__image {
      user-select: none;
      pointer-events: none;
    }

    &__slide {
      width: 100vw;
      height: 100vh;
      display: flex;
      justify-content: center;
      align-items: center;

      cursor: pointer;

      &.full-width {
        #{ $block }__image {
          width: 100%;
          height: 100%;
          object-fit: cover;

          @include screen-size("small") {
            width: 90%;
            height: 90%;
            object-fit: contain;
          }
        }
      }

      &.proportional {
        #{ $block}__image {
          width: 90%;
          height: 90%;
          object-fit: contain;
        }
      }
    }

    &__caption {
      position: absolute;
      bottom: 0px;
      padding-left: 10%;
      padding-right: 10%;
      width: 100%;
      background: $white;
      padding: $main-margin;
      line-height: 1.3em;
      font-size: $font-size-small;
      font-family: $serif-stack;
      opacity: 1;
      text-align: center;
      background: white;
      user-select: none;

      @include screen-size("small") {
        display: none;
      }
    }
  }

  .navigation {
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 50%;
    height: 60px;
    margin-top: -30px;
    z-index: 3000;
    cursor: pointer;
    transition: transform 0.3s $easing;

    @include screen-size("small") {
      height: 40px;
      top: unset;
      bottom: 30px;
    }

    &:hover {
      transform: scale(1.1);
    }
  }

  .previous {
    left: 10px;
  }

  .next {
    right: 10px;
  }

  .arrow {
    height: 100%;
  }

  .close {
    position: fixed;
    top: 10px;
    right: 20px;
    z-index: 3000;
    height: 50px;
    width: 50px;
    opacity: 0;
    pointer-events: none;

    @include screen-size("small") {
      height: 40px;
      width: 40px;
    }

    cursor: pointer;
    transition: transform $fast-easing;

    &:hover {
      transform: scale(1.1);
    }
  }

  .cross {
    height: 100%;
    line {
      stroke: $black;
      stroke-width: 2px;
    }
  }

  .active {
    opacity: 1;
    pointer-events: all;
  }
</style>

<div class="carousel slideshow fullscreen" class:active bind:this={slideShowEl}>
  {#each paddedSlides as slide}
    <div
      class="carousel-cell slideshow__slide"
      class:full-width={slide.fullwidth == 1}
      class:proportional={slide.fullwidth == 0}>
      {#if slide.video}
        <Video url={slide.video} />
      {:else}
        <img
          class="slideshow__image"
          srcset="{slide.sizes.small.url}
          {slide.sizes.small.width}w, {slide.sizes.medium.url}
          {slide.sizes.medium.width}w, {slide.sizes.large.url}
          {slide.sizes.large.width}w, {slide.sizes.xlarge.url}
          {slide.sizes.xlarge.width}w"
          sizes="100vw"
          src={slide.sizes.large.url}
          alt={slide.caption ? slide.caption : 'Complete Works'} />
      {/if}
      {#if slide.caption}
        <div class="slideshow__caption">{slide.caption}</div>
      {/if}

    </div>
  {/each}
</div>

<!-- {# NAVIGATION: PREVIOUS #} -->
<div
  class="navigation previous"
  class:active
  type="button"
  aria-label="Previous"
  on:click={e => {
    flkty.previous(true);
  }}>
  <svg class="arrow" viewBox="0 0 62.68 55">
    <path
      d="M5.64,27.05c2.31-2.37,8.66-9,14.19-14.89L30.47.9H26.84c-6,6.35-18.48,20.31-25.49,27.5,7.09,7,19.55,21.16,25.49,27.5h3.63q-5.7-6.21-10.64-11.42C14.38,38.47,8,31.79,5.64,29.59H64V27.05Z"
      transform="translate(-1.35 -0.9)" />
  </svg>
</div>

<!-- {# NAVIGATION: NEXT #} -->
<div
  class="navigation next"
  class:active
  type="button"
  aria-label="Previous"
  on:click={e => {
    flkty.next(true);
  }}>
  <svg class="arrow" viewBox="0 0 62.68 55">
    <path
      d="M59.75,29.76c-2.31,2.37-8.66,9-14.19,14.89L34.92,55.9h3.63C44.57,49.56,57,35.59,64,28.4c-7.1-7-19.55-21.15-25.49-27.5H34.92q5.69,6.23,10.64,11.43C51,18.33,57.35,25,59.75,27.22H1.35v2.54Z"
      transform="translate(-1.35 -0.9)" />
  </svg>
</div>

<!-- {# NAVIGATION: CLOSE #} -->
<div class="close js-close" class:active on:click={closeSlideshow}>
  <svg class="cross" viewBox="0 0 44 44">
    <line x1="0.71" y1="0.71" x2="43.29" y2="43.29" />
    <line x1="0.71" y1="43.29" x2="43.29" y2="0.71" />
  </svg>
</div>
